import { useState, useEffect, useCallback } from 'react';
import { ListSubheader, MenuItem } from '@mui/material';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../config';

/**
 * useAssignableUsers — fetches GET /api/assignable-users?organisation_id=&contract_id=
 * Returns { staff: [{id, name}], vendorCrew: [{user_id, member_id, name, designation, vendor_name, service_provider_id}] }.
 *
 * Pass `contractId` to have the backend pre-filter vendor crew to that contract's vendor.
 * For client-side filtering by a known service_provider_id, use filterVendorCrewByProvider instead.
 */
export default function useAssignableUsers(organisationId, contractId) {
  const [staff, setStaff] = useState([]);
  const [vendorCrew, setVendorCrew] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAssignableUsers = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const params = { organisation_id: organisationId };
      if (contractId) params.contract_id = contractId;
      const res = await axios.get(`${BASE_URL}/api/assignable-users`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaff(res.data?.staff || []);
      setVendorCrew(res.data?.vendor_crew || []);
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    } finally {
      setLoading(false);
    }
  }, [organisationId, contractId]);

  useEffect(() => { fetchAssignableUsers(); }, [fetchAssignableUsers]);

  return { staff, vendorCrew, loading, refetch: fetchAssignableUsers };
}

/**
 * Client-side filter of an already-fetched vendorCrew list to a single vendor
 * (service_provider_id). Returns the full list unchanged if no id is given.
 */
export function filterVendorCrewByProvider(vendorCrew, serviceProviderId) {
  if (!serviceProviderId) return vendorCrew;
  return vendorCrew.filter(v => v.service_provider_id === serviceProviderId);
}

/**
 * Renders "Internal staff" / "Vendor crew" grouped MenuItems as a flat array
 * (required by MUI Select — fragments break selection). Callers own the
 * "Unassigned"/placeholder MenuItem, if any, and prepend it themselves.
 */
export function renderAssignableMenuItems(staff, vendorCrew) {
  const items = [];
  if (staff.length) {
    items.push(<ListSubheader key="_staff_header">Internal staff</ListSubheader>);
    staff.forEach(u => items.push(<MenuItem key={`staff-${u.id}`} value={u.id}>{u.name}</MenuItem>));
  }
  if (vendorCrew.length) {
    items.push(<ListSubheader key="_crew_header">Vendor crew</ListSubheader>);
    vendorCrew.forEach(v => items.push(
      <MenuItem key={`crew-${v.user_id}`} value={v.user_id}>{v.name} — {v.vendor_name}</MenuItem>
    ));
  }
  return items;
}
